import { CoreImageView, CoreTextView } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { Glass, type GlassOptics } from '@samasante/liquid-glass';
import clsx from 'clsx';
import React from 'react';
import { getSemanticColors, SemanticColor, TColorVarsMap, toColorVars } from '../../../theme/colors';
import { TextStyle } from '../../../types/styles';
import { getInlineStyles } from '../../../utils/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { Tag } from '../../UI/Tag/Tag';
import styles from './paysendCard.module.css';

const CARD_GLASS_OPTICS: Partial<GlassOptics> = {
    mapSize: 256,
    frost: 14,
    saturate: 1.2,
    strength: 0.03,
    depth: 0.24,
    curvature: 0.6,
    bend: 0.5,
    bendWidth: 0.1,
    dispersion: 0.28,
    sheen: 0.42,
    sheenWidth: 4,
    glow: 0.14,
    brightness: 0.04,
};

const PAYSEND_CARD_COLOR_VARS = {
    '--paysend-card-bg': SemanticColor.FillSecondaryTransparent,
    '--paysend-card-bg-hovered': SemanticColor.FillSecondaryTransparentHovered,
    '--paysend-card-bg-pressed': SemanticColor.FillSecondaryTransparentPressed,
    '--paysend-card-frozen-overlay': SemanticColor.OverlayPrimary,
} as const satisfies TColorVarsMap;

type TPaysendCard = {
    isCardScreen?: boolean;
    isFrozen?: boolean;
    isExpired?: boolean;
    isKycInProgress?: boolean;
    appleWalletAdded?: boolean;
    googleWalletAdded?: boolean;
    getCard?: string;
    metaText?: string;
    isFirstRender?: boolean;
    isCardIssued?: boolean;
    /** Design version of the owning widget. */
    version: number | undefined;
};

export const PaysendCard = ({
    isCardScreen,
    isFrozen,
    isExpired,
    isKycInProgress,
    appleWalletAdded,
    googleWalletAdded,
    metaText,
    getCard,
    isFirstRender,
    isCardIssued,
    version,
}: TPaysendCard) => {
    const semanticColors = getSemanticColors(version);
    const isInactive = isFrozen || isExpired || isKycInProgress;
    return (
        <Glass
            className={clsx(
                styles.cardContainer,
                isCardScreen && styles.cardContainerOnCardScreen,
                isCardScreen ? styles.cardAnimationZoomIn : styles.cardAnimationZoomOut,
                isFrozen && styles.frozen,
                isExpired && styles.expired,
                isFirstRender && styles.firstRender,
                isInactive && styles.cardContainerInactive,
            )}
            optics={CARD_GLASS_OPTICS}
            style={{
                display: 'flex',
                ...toColorVars(PAYSEND_CARD_COLOR_VARS, version),
                ...getInlineStyles({
                    variables: {
                        'card-background-image': `url(${getHref('ic_psCardBalance', version)})`,
                    },
                }),
            }}
        >
            <div className={styles.cardSurface}>
                <div className={clsx(styles.cardRow, styles.topAlignment, styles.flexEnd)}>
                    {isInactive && (
                        <div className={styles.tagContainer}>
                            <Tag
                                version={version}
                                metaVariant={'tag'}
                                metaHierarchy={'inverted'}
                                size={isCardScreen ? 'default' : 'small'}
                                metaText={metaText}
                            />
                        </div>
                    )}
                </div>
                <div className={clsx(styles.cardRow, isCardIssued && styles.flexEnd)}>
                    <ConditionalRender condition={!isCardIssued && !isKycInProgress}>
                        <CoreTextView text={getCard} as="span" styleProps={{ ...TextStyle.main, main: semanticColors[SemanticColor.TextPrimaryOnDarkFill] }} />
                        <VisualView
                            version={version}
                            iconName={'ic_arrow_forward_sharp'}
                            iconHierarchy={'emphasised'}
                            iconColor={semanticColors[SemanticColor.FillSecondaryOnDark]}
                        />
                    </ConditionalRender>
                    <ConditionalRender condition={isCardIssued}>
                        <div className={clsx(styles.paymentIconsRow, isCardScreen && styles.paymentIconsRowCardScreen)}>
                            {appleWalletAdded && (
                                <div className={styles.iconContainer}>
                                    <CoreImageView
                                        src={getHref('ic_apple_pay_small', version)}
                                        styleProps={{
                                            width: isCardScreen ? 32 : 21,
                                        }}
                                    />
                                </div>
                            )}
                            {googleWalletAdded && (
                                <div className={styles.iconContainer}>
                                    <CoreImageView
                                        src={getHref('ic_google_pay_small', version)}
                                        styleProps={{
                                            width: isCardScreen ? 38 : 25,
                                        }}
                                    />
                                </div>
                            )}
                            <div className={styles.iconContainer}>
                                <CoreImageView
                                    src={getHref('mastercard', version)}
                                    styleProps={{
                                        width: isCardScreen ? 40 : 27,
                                    }}
                                />
                            </div>
                        </div>
                    </ConditionalRender>
                </div>
            </div>
            {(isFrozen || isExpired) && <div className={styles.frozenFrost} aria-hidden={true} />}
        </Glass>
    );
};
